/**
 * Corrects the damage Chameleon's push-mode banner does to `position: fixed`
 * elements.
 *
 * When a push-mode banner is up, Chameleon makes room for it by walking the
 * document for fixed elements and rewriting their geometry inline with
 * `!important`. That is right for top-anchored chrome and wrong in two ways we
 * have to undo:
 *
 * 1. **Bottom-anchored elements** (the floating action bar) get shifted *down*
 *    by the banner height and land past the bottom edge, out of reach.
 * 2. **Full-viewport containers** (the antd modal wrap) get their `top` pushed
 *    down, but their height cap still assumes a full viewport, so the modal
 *    runs off the bottom of the screen and its footer becomes unreachable.
 */

/**
 * The offset the modal subtracts from its `max-height` calculations to account
 * for the Chameleon banner. `BTModal.less` reads this CSS custom property and
 * defaults it to `0px`, so when nothing publishes a value the caps behave
 * exactly as they did before Chameleon was accounted for.
 */
const OFFSET_VAR = "--chameleon-banner-height";

/**
 * antd renders every modal inside this fixed, full-viewport scroll container.
 * It is the element Chameleon displaces, so its inline `top` is the offset the
 * modal's height cap has to give back.
 */
const MODAL_WRAP_SELECTOR = ".ant-modal-wrap";

/**
 * Whether Chameleon - rather than our own styling - set `property` inline on
 * `element`.
 *
 * `!important` is the tell: React cannot set priority through the `style` prop
 * and none of our components write inline priority by hand, so an `!important`
 * inline declaration on an element we render can only be Chameleon's.
 */
function isPushedByChameleon(element: HTMLElement, property: string): boolean {
    const value = element.style.getPropertyValue(property);
    if (value === "" || value === "none") {
        return false;
    }

    return element.style.getPropertyPriority(property) === "important";
}

/**
 * Whether Chameleon's push has driven `element` off the bottom of the viewport.
 */
function isPushedOffScreen(element: HTMLElement): boolean {
    if (window.getComputedStyle(element).position !== "fixed") {
        return false;
    }

    const viewportHeight = window.innerHeight;
    if (viewportHeight === 0) {
        return false;
    }

    // A partly-clipped element is still reachable; only flag one whose bottom
    // edge has cleared the fold. The tolerance absorbs subpixel rounding.
    return element.getBoundingClientRect().bottom > viewportHeight + 1;
}

/**
 * The offset Chameleon has applied to the displayed modal, or 0 when no modal
 * is pushed.
 */
function readModalOffset(): number {
    let offset = 0;

    document.querySelectorAll<HTMLElement>(MODAL_WRAP_SELECTOR).forEach((wrap) => {
        const top = Number.parseFloat(wrap.style.top);
        if (Number.isFinite(top) && top > offset) {
            offset = top;
        }
    });

    return offset;
}

/** Whether any of `nodes` is, or contains, an antd modal wrap. */
function containsModalWrap(nodes: NodeList): boolean {
    let found = false;

    nodes.forEach((node) => {
        if (found || !(node instanceof HTMLElement)) {
            return;
        }
        found =
            node.matches(MODAL_WRAP_SELECTOR) || node.querySelector(MODAL_WRAP_SELECTOR) !== null;
    });

    return found;
}

/**
 * Starts watching for Chameleon's push and correcting it.
 *
 * @returns A teardown that stops watching and clears the published offset.
 */
export function startChameleonPushCorrection(): () => void {
    const root = document.documentElement;

    let lastOffset: number | undefined;

    const syncModalOffset = (): void => {
        const offset = readModalOffset();
        // Writing on every mutation would dirty the style attribute constantly;
        // only touch the DOM when the value actually moves.
        if (offset === lastOffset) {
            return;
        }
        lastOffset = offset;
        root.style.setProperty(OFFSET_VAR, `${offset}px`);
    };

    // Elements already rescued this push, so Chameleon clearing the transform -
    // itself a style mutation - does not trigger a second dispatch. Entries drop
    // out as soon as the transform clears, which re-arms the element for a later
    // banner instead of giving up on it for the rest of its lifetime.
    const recovered = new WeakSet<HTMLElement>();

    /**
     * @returns Whether this element newly needs Chameleon to re-run its layout
     * pass. Callers batch the dispatch so one mutation batch resizes once.
     */
    const needsRecovery = (element: HTMLElement): boolean => {
        if (!isPushedByChameleon(element, "transform")) {
            recovered.delete(element);
            return false;
        }

        if (recovered.has(element) || !isPushedOffScreen(element)) {
            return false;
        }

        recovered.add(element);
        return true;
    };

    const handleMutations = (records: MutationRecord[]): void => {
        let modalsChanged = false;
        let shouldResize = false;

        records.forEach((record) => {
            if (record.type === "childList") {
                modalsChanged ||=
                    containsModalWrap(record.addedNodes) || containsModalWrap(record.removedNodes);
                return;
            }

            const target = record.target;
            if (!(target instanceof HTMLElement)) {
                return;
            }

            if (target.matches(MODAL_WRAP_SELECTOR)) {
                modalsChanged = true;
                return;
            }

            // Every inline-style write in the app lands here, so the cheap
            // priority check in `isPushedByChameleon` has to come first - the
            // computed-style read only happens for the rare element Chameleon
            // has actually stamped.
            const needsResize = needsRecovery(target);
            shouldResize = shouldResize || needsResize;
        });

        if (modalsChanged) {
            syncModalOffset();
        }

        if (shouldResize) {
            window.dispatchEvent(new Event("resize"));
        }
    };

    // Two observers rather than one: the push can land on any element, but modal
    // portals mount as direct children of `document.body`, so the childList half
    // does not need `subtree` and never scans the app's own DOM churn.
    const styleObserver = new MutationObserver(handleMutations);
    styleObserver.observe(document.body, {
        attributes: true,
        attributeFilter: ["style"],
        subtree: true,
    });

    const portalObserver = new MutationObserver(handleMutations);
    portalObserver.observe(document.body, { childList: true });

    // Chameleon may have stamped both the offset and the transforms before this
    // ran, in which case no mutation is coming and the observers alone would
    // never fire. The attribute filter narrows the sweep to elements that carry
    // an inline transform at all, which is a small fraction of the document.
    syncModalOffset();

    // `filter`, not `some`: every pushed element has to be marked recovered, so
    // short-circuiting on the first would leave the rest to dispatch again.
    const pushedOnStartup = Array.from(
        document.querySelectorAll<HTMLElement>('[style*="transform"]'),
    ).filter(needsRecovery);

    if (pushedOnStartup.length > 0) {
        window.dispatchEvent(new Event("resize"));
    }

    return () => {
        styleObserver.disconnect();
        portalObserver.disconnect();
        // Reset rather than remove: a stale offset left behind would make every
        // later modal short by the height of a banner that is no longer there.
        root.style.setProperty(OFFSET_VAR, "0px");
    };
}
